// Routing utilities for Pinaki Solutions CRM

export const ROUTE_MAP = {
  Dashboard: "/dashboard",
  Activities: "/activities",
  "Tasks & Reports": "/tasks",
  Tasks: "/tasks",
  Orders: "/orders",
  Clients: "/clients",
  Leads: "/leads",
  Procurement: "/procurement",
  Design: "/design",
  Approvals: "/approvals",
  Production: "/production",
  Dispatch: "/dispatch",
  Challans: "/dispatch",
  Delivery: "/delivery",
  Accounts: "/accounts",
  ClientAccounts: "/accounts/client",
  Invoices: "/accounts",
  Payments: "/accounts",
  Reports: "/reports",
  "Users & Roles": "/users-roles",
  Settings: "/settings",
  Profile: "/profile",
};

/**
 * Parse a given pathname and optional search query into CRM route parameters.
 * @param {string} pathname - e.g. "/design/PS-1001" or "/clients/CL-52F98C" or "/orders"
 * @param {string} search - e.g. "?tab=Production"
 */
export function parseRoute(pathname = "", search = "") {
  const rawClean = (pathname || "/").trim().replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(search || "");
  const tab = params.get("tab") || null;
  const stage = params.get("stage") || null;
  const statusFilter = params.get("status") || params.get("statusFilter") || null;
  const dateFilter = params.get("dateFilter") || params.get("timeframe") || null;
  const filter = params.get("filter") || null;
  const month = params.get("month") || null;
  const company = params.get("company") || null;
  const activeTab = params.get("activeTab") || tab || null;

  const filterParams = {
    tab,
    stage,
    statusFilter,
    dateFilter,
    filter,
    month,
    company,
    activeTab,
  };

  // 1. Public sign-off and payment routes
  const approveMatch = rawClean.match(/^\/approve\/(.+)$/i);
  if (approveMatch) {
    return { isPublic: true, page: "ApprovalPage", token: approveMatch[1] };
  }

  const payMatch = rawClean.match(/^\/pay\/(.+)$/i);
  if (payMatch) {
    return { isPublic: true, page: "PayPage", token: payMatch[1] };
  }

  // 1b. User Detail: /users/:userId (must come before /users-roles base route)
  const userDetailMatch = rawClean.match(/^\/users\/([^/]+)$/i);
  if (userDetailMatch) {
    const rawId = decodeURIComponent(userDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "users" && rawId.toLowerCase() !== "roles" && rawId.toLowerCase() !== "users-roles") {
      return {
        page: "UserDetail",
        userId: rawId,
        selectedUserId: rawId,
      };
    }
  }

  // 1c. Lead Detail: /leads/:leadId (must come before /leads base route)
  const leadDetailMatch = rawClean.match(/^\/leads\/([^/]+)$/i);
  if (leadDetailMatch) {
    const rawId = decodeURIComponent(leadDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "leads") {
      return {
        page: "LeadDetail",
        leadId: rawId,
        selectedLeadId: rawId,
      };
    }
  }

  // 1d. Task Detail: /tasks/:taskId (must come before /tasks base route)
  const taskDetailMatch = rawClean.match(/^\/tasks\/([^/]+)$/i);
  if (taskDetailMatch) {
    const rawId = decodeURIComponent(taskDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "tasks" && rawId.toLowerCase() !== "reports") {
      return {
        page: "TaskDetail",
        taskId: rawId,
        selectedTaskId: rawId,
      };
    }
  }

  // 2a. Design Batch Detail: /design/batch/:batchId or /designs/batch/:batchId
  const designBatchMatch = rawClean.match(/^\/designs?\/batch\/([^/]+)$/i);
  if (designBatchMatch) {
    const rawBatchId = decodeURIComponent(designBatchMatch[1]);
    return {
      page: "DesignDetail",
      batchId: rawBatchId,
      designBatchId: rawBatchId,
      orderId: rawBatchId,
      selectedOrderId: rawBatchId,
      selectedDesignOrderId: rawBatchId,
    };
  }

  // 2b. Design Detail: /design/:orderId or /designs/:orderId
  const designDetailMatch = rawClean.match(/^\/designs?\/([^/]+)$/i);
  if (designDetailMatch) {
    const rawId = decodeURIComponent(designDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "design" && rawId.toLowerCase() !== "designs") {
      const isBatch = rawId.toUpperCase().startsWith("BATCH-");
      return {
        page: "DesignDetail",
        orderId: rawId,
        designOrderId: rawId,
        selectedOrderId: rawId,
        selectedDesignOrderId: rawId,
        batchId: isBatch ? rawId : null,
      };
    }
  }

  // 3a. Production Batch Detail: /production/batch/:batchId
  const productionBatchMatch = rawClean.match(/^\/production\/batch\/([^/]+)$/i);
  if (productionBatchMatch) {
    const rawBatchId = decodeURIComponent(productionBatchMatch[1]);
    return {
      page: "ProductionDetail",
      batchId: rawBatchId,
      productionBatchId: rawBatchId,
      orderId: rawBatchId,
      selectedOrderId: rawBatchId,
      selectedProductionOrderId: rawBatchId,
    };
  }

  // 3b. Production Detail: /production/:orderId
  const productionDetailMatch = rawClean.match(/^\/production\/([^/]+)$/i);
  if (productionDetailMatch) {
    const rawId = decodeURIComponent(productionDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "production") {
      const isBatch = rawId.toUpperCase().startsWith("BATCH-");
      return {
        page: "ProductionDetail",
        orderId: rawId,
        productionOrderId: rawId,
        selectedOrderId: rawId,
        selectedProductionOrderId: rawId,
        batchId: isBatch ? rawId : null,
      };
    }
  }

  // 3b-1. Dispatch Batch Detail: /dispatch/batch/:batchId
  const dispatchBatchMatch = rawClean.match(/^\/dispatch\/batch\/([^/]+)$/i);
  if (dispatchBatchMatch) {
    const rawBatchId = decodeURIComponent(dispatchBatchMatch[1]);
    return {
      page: "DispatchDetail",
      batchId: rawBatchId,
      dispatchBatchId: rawBatchId,
      orderId: rawBatchId,
      selectedOrderId: rawBatchId,
      selectedDispatchOrderId: rawBatchId,
    };
  }

  // 3b-2. Dispatch Detail: /dispatch/:orderId
  const dispatchDetailMatch = rawClean.match(/^\/dispatch\/([^/]+)$/i);
  if (dispatchDetailMatch) {
    const rawId = decodeURIComponent(dispatchDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "dispatch") {
      const isBatch = rawId.toUpperCase().startsWith("BATCH-");
      return {
        page: "DispatchDetail",
        orderId: rawId,
        dispatchOrderId: rawId,
        selectedOrderId: rawId,
        selectedDispatchOrderId: rawId,
        batchId: isBatch ? rawId : null,
      };
    }
  }

  // 3c. Delivery Person Queue: /delivery/person/:personId or /delivery/driver/:personId
  const deliveryPersonMatch = rawClean.match(/^\/delivery\/(?:persons?|drivers?|agents?)\/([^/]+)$/i);
  if (deliveryPersonMatch) {
    const rawId = decodeURIComponent(deliveryPersonMatch[1]);
    if (rawId && rawId.toLowerCase() !== "delivery" && rawId.toLowerCase() !== "person" && rawId.toLowerCase() !== "persons") {
      return {
        page: "DeliveryPersonDetail",
        personId: rawId,
        selectedDeliveryPersonId: rawId,
      };
    }
  }

  // 3d. Delivery Detail: /delivery/:orderId
  const deliveryDetailMatch = rawClean.match(/^\/delivery\/([^/]+)$/i);
  if (deliveryDetailMatch) {
    const rawId = decodeURIComponent(deliveryDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "delivery") {
      return {
        page: "DeliveryDetail",
        orderId: rawId,
        deliveryOrderId: rawId,
        selectedOrderId: rawId,
      };
    }
  }

  // 4a. Client Orders: /orders/client/:clientId
  const clientOrdersMatch = rawClean.match(/^\/orders\/clients?\/([^/]+)$/i);
  if (clientOrdersMatch) {
    const rawId = decodeURIComponent(clientOrdersMatch[1]);
    if (rawId && rawId.toLowerCase() !== "orders" && rawId.toLowerCase() !== "client" && rawId.toLowerCase() !== "clients") {
      return {
        page: "ClientOrders",
        clientId: rawId,
        selectedClientId: rawId,
      };
    }
  }

  // 4b. Order Detail: /orders/:orderId
  const orderDetailMatch = rawClean.match(/^\/orders\/([^/]+)$/i);
  if (orderDetailMatch) {
    const rawId = decodeURIComponent(orderDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "orders") {
      return {
        page: "OrderDetail",
        orderId: rawId,
        selectedOrderId: rawId,
        tab,
      };
    }
  }

  // 5. Client Detail: /clients/:clientId
  const clientDetailMatch = rawClean.match(/^\/clients\/([^/]+)$/i);
  if (clientDetailMatch) {
    const rawId = decodeURIComponent(clientDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "clients") {
      return {
        page: "ClientDetail",
        clientId: rawId,
        selectedClientId: rawId,
      };
    }
  }

  // 5b. Procurement Request Detail: /procurement/:requestId
  const procurementDetailMatch = rawClean.match(/^\/procurement\/([^/]+)$/i);
  if (procurementDetailMatch) {
    const rawId = decodeURIComponent(procurementDetailMatch[1]);
    if (rawId && rawId.toLowerCase() !== "procurement" && rawId.toLowerCase() !== "procurements") {
      return {
        page: "ProcurementDetail",
        requestId: rawId,
        selectedRequestId: rawId,
      };
    }
  }

  // 5c. Client Accounts: /accounts/client/:clientId or /accounts/clients/:clientId
  const clientAccountsMatch = rawClean.match(/^\/accounts\/clients?\/([^/]+)$/i);
  if (clientAccountsMatch) {
    const rawId = decodeURIComponent(clientAccountsMatch[1]);
    if (rawId && rawId.toLowerCase() !== "accounts" && rawId.toLowerCase() !== "client" && rawId.toLowerCase() !== "clients") {
      return {
        page: "ClientAccounts",
        clientId: rawId,
        selectedClientId: rawId,
      };
    }
  }

  // 6. Base / Module Level Routes
  const clean = rawClean.toLowerCase();
  if (clean === "" || clean === "/" || clean === "/dashboard") {
    return { page: "Dashboard" };
  }
  if (clean === "/activities" || clean === "/activity") return { page: "Activities", ...filterParams };
  if (clean === "/tasks" || clean === "/tasks-reports" || clean === "/tasks-and-reports") return { page: "Tasks & Reports", ...filterParams };
  if (clean === "/orders") return { page: "Orders", ...filterParams };
  if (clean === "/design" || clean === "/designs") return { page: "Design", ...filterParams };
  if (clean === "/clients") return { page: "Clients", ...filterParams };
  if (clean === "/leads") return { page: "Leads", ...filterParams };
  if (clean === "/procurement" || clean === "/procurements") return { page: "Procurement", ...filterParams };
  if (clean === "/approvals" || clean === "/approval") return { page: "Approvals", ...filterParams };
  if (clean === "/production") return { page: "Production", ...filterParams };
  if (clean === "/dispatch" || clean === "/challans") return { page: "Dispatch", ...filterParams };
  if (clean === "/delivery") return { page: "Delivery", ...filterParams };
  if (clean === "/accounts" || clean === "/invoices" || clean === "/payments") return { page: "Accounts", ...filterParams };
  if (clean === "/reports") return { page: "Reports", ...filterParams };
  if (clean === "/users" || clean === "/roles" || clean === "/users-roles") {
    return { page: "Users & Roles", ...filterParams };
  }
  if (clean === "/settings") return { page: "Settings", ...filterParams };
  if (clean === "/profile") return { page: "Profile", ...filterParams };

  // Fallback for unrecognized routes
  return { page: "Dashboard", isNotFound: true };
}

/**
 * Build the canonical URL string for a given route specification.
 */
export function getPathForRoute(params = {}) {
  if (typeof params === "string") {
    return ROUTE_MAP[params] || `/${params.toLowerCase()}`;
  }

  const {
    page,
    orderId,
    selectedOrderId,
    designOrderId,
    productionOrderId,
    dispatchOrderId,
    deliveryOrderId,
    personId,
    selectedDeliveryPersonId,
    clientId,
    selectedClientId,
    leadId,
    selectedLeadId,
    userId,
    selectedUserId,
    taskId,
    selectedTaskId,
    requestId,
    selectedRequestId,
    tab,
  } = params;

  const oId = orderId || selectedOrderId;
  const dId = designOrderId || oId;
  const pId = productionOrderId || oId;
  const dpId = dispatchOrderId || oId;
  const dlId = deliveryOrderId || oId;
  const dpPersonId = selectedDeliveryPersonId || personId;
  const cId = selectedClientId || clientId;
  const lId = selectedLeadId || leadId;
  const uId = selectedUserId || userId;
  const tId = selectedTaskId || taskId;
  const rId = selectedRequestId || requestId;

  if (page === "DeliveryPersonDetail" && dpPersonId) {
    return `/delivery/person/${encodeURIComponent(dpPersonId)}`;
  }

  if (page === "TaskDetail" && tId) {
    return `/tasks/${encodeURIComponent(tId)}`;
  }
  if (page === "UserDetail" && uId) {
    return `/users/${encodeURIComponent(uId)}`;
  }
  if (page === "LeadDetail" && lId) {
    return `/leads/${encodeURIComponent(lId)}`;
  }
  if (page === "ClientOrders" && cId) {
    return `/orders/client/${encodeURIComponent(cId)}`;
  }
  if (page === "ClientDetail" && cId) {
    return `/clients/${encodeURIComponent(cId)}`;
  }
  if (page === "ProcurementDetail" && rId) {
    return `/procurement/${encodeURIComponent(rId)}`;
  }
  if (page === "OrderDetail" && oId) {
    const base = `/orders/${encodeURIComponent(oId)}`;
    return tab ? `${base}?tab=${encodeURIComponent(tab)}` : base;
  }
  if (page === "DesignDetail") {
    if (params.batchId) return `/design/batch/${encodeURIComponent(params.batchId)}`;
    if (dId) {
      if (typeof dId === "string" && dId.startsWith("BATCH-")) {
        return `/design/batch/${encodeURIComponent(dId)}`;
      }
      return `/design/${encodeURIComponent(dId)}`;
    }
  }
  if (page === "ProductionDetail") {
    if (params.batchId) return `/production/batch/${encodeURIComponent(params.batchId)}`;
    if (pId) {
      if (typeof pId === "string" && pId.startsWith("BATCH-")) {
        return `/production/batch/${encodeURIComponent(pId)}`;
      }
      return `/production/${encodeURIComponent(pId)}`;
    }
  }
  if (page === "DispatchDetail") {
    if (params.batchId) return `/dispatch/batch/${encodeURIComponent(params.batchId)}`;
    if (dpId) {
      if (typeof dpId === "string" && dpId.startsWith("BATCH-")) {
        return `/dispatch/batch/${encodeURIComponent(dpId)}`;
      }
      return `/dispatch/${encodeURIComponent(dpId)}`;
    }
  }
  if (page === "DeliveryDetail" && dlId) {
    return `/delivery/${encodeURIComponent(dlId)}`;
  }
  if (page === "ClientAccounts" && cId) {
    return `/accounts/client/${encodeURIComponent(cId)}`;
  }

  let basePath = null;
  if (ROUTE_MAP[page]) {
    basePath = ROUTE_MAP[page];
  } else if (page === "Tasks") basePath = "/tasks";
  else if (page === "Challans") basePath = "/dispatch";
  else if (page === "Invoices" || page === "Payments") basePath = "/accounts";

  if (basePath) {
    const queryParams = new URLSearchParams();
    if (tab) queryParams.set("tab", tab);
    if (params.activeTab && params.activeTab !== tab) queryParams.set("activeTab", params.activeTab);
    if (params.stage) queryParams.set("stage", params.stage);
    if (params.statusFilter) queryParams.set("status", params.statusFilter);
    if (params.dateFilter && params.dateFilter !== "all") queryParams.set("dateFilter", params.dateFilter);
    if (params.filter) queryParams.set("filter", params.filter);
    if (params.month) queryParams.set("month", params.month);
    if (params.company && params.company !== "All Companies") queryParams.set("company", params.company);

    const qs = queryParams.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  }

  return "/dashboard";
}

